import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type DragEvent,
} from 'react'
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Code2,
  FileArchive,
  FileText,
  Github,
  KeyRound,
  LayoutTemplate,
  Link2,
  Loader2,
  MonitorPlay,
  Rocket,
  Sparkles,
  UploadCloud,
  X,
} from 'lucide-react'
import type { CogoXyzInputType, CogoXyzSourceType } from '@cogo/types-v2'
import { PageHeader } from '@/components/page-header'
import { GuidanceDisclosure } from '../components/guidance-disclosure'
import { CogoApplicationImportButton } from '@/features/project/components/cogo-application-import-button'
import { cogoXyzRegistry } from '@/features/cogo-xyz/cogo-xyz-registry'
import { loadCogoXyzRegistryFromCurrentProject } from '@/features/cogo-xyz/cogo-xyz-project-storage'
import {
  buildFigmaTokenControlFromImportError,
  buildFigmaTokenControlFromStoredToken,
  clearStoredFigmaAccessToken,
  isFigmaFrameImportAvailable,
  parseFigmaFrameReference,
  readStoredFigmaAccessToken,
  saveStoredFigmaAccessToken,
  type FigmaTokenControl,
  type FigmaTokenValidationResult,
} from '@/features/cogo-xyz/figma-token-connection'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { pushStudioRoute } from '@/lib/studio-navigation'
import { useStudioStore } from '@/store/studio-store'
import {
  importOpenPencilFigmaFile,
  isOpenPencilFigmaImportAvailable,
} from '@/features/openpencil-figma/openpencil-figma-client'
import { activateOpenPencilSourceWorkspace } from '@/features/openpencil-figma/openpencil-source-navigation'
import {
  figmaFrameNeedsOpenProject,
  getSourceDoorsCopy,
} from './source-doors-copy'
import { OpenPencilFigmaSourcePanels } from '@/features/openpencil-figma/openpencil-figma-source-panels'
import type { OpenPencilFigmaSourceBundle } from '@/features/openpencil-figma/openpencil-figma-source'
import { checkOpenPencilSourceFile } from '@/features/openpencil-figma/openpencil-source-file-gate'
import {
  classifyOpenPencilImportFailureText,
  type OpenPencilImportFailureReason,
} from '@/features/openpencil-figma/openpencil-import-failure'
import { getOpenPencilImportFailureCopy } from '@/features/openpencil-figma/openpencil-import-failure-copy'
import { OpenPencilImportFailureNotice } from '@/features/openpencil-figma/openpencil-import-failure-notice'
import { useStudioLocale } from '@/i18n'
import { DashboardContainer } from '../components/dashboard-container'
import { DocxSourceViewer } from '@/features/source-document/docx-source-viewer'
import {
  DOCUMENT_SOURCE_ACCEPT,
  DOCUMENT_SOURCE_EXTENSIONS,
  documentFormatForFileName,
  fileExtensionOf,
  type DocumentViewerLoadResult,
} from '@/features/source-document/document-source-model'
import {
  getDocxViewerSession,
  openDocumentSource,
  restoreDocumentSource,
  subscribeDocxViewerSession,
  type DocumentSourceOpenResult,
} from '@/features/source-document/docx-source-viewer-state'
import {
  clearPendingSourceDrop,
  readPendingSourceDrop,
} from '@/features/source-document/pending-source-drop'
import { getSourceDropCopy } from '../source-drop-copy'
import { getDocxSourceViewerCopy } from '@/features/source-document/docx-source-viewer-copy'
import { getSourceSurfaceCopy } from '@/shell/ide-v2/surfaces/source-surface-copy'
import { SourceIngestGuidancePanel } from '@/features/source-ingest/source-ingest-guidance-panel'
import {
  buildSourceIngestGuidance,
  detectStudioSourceKind,
  explainSourceOperationError,
  sourcePurposeTag,
  type SourcePurpose,
  type StudioSourceKind,
} from '@/features/source-ingest/source-ingest-model'
import { getSourceIngestCopy } from '@/features/source-ingest/source-ingest-copy'

type ImportSource = StudioSourceKind

interface SourceOption {
  id: ImportSource
  label: string
  description: string
  bestFor: string
  icon: typeof Sparkles
  output: string
}

interface LibraryRemixDraft {
  workItemId: string
  sourceTitle: string
  title: string
  input: string
  tags: string
  audience: string
}

const SOURCE_OPTIONS: SourceOption[] = [
  {
    id: 'assistant_prompt',
    label: 'Describe it',
    description: 'Write the app, document, or report you want.',
    bestFor: 'Starting from an idea',
    icon: Sparkles,
    output: 'Draft in Library',
  },
  {
    id: 'url_import',
    label: 'Use a website',
    description: 'Bring in a page URL as a reference.',
    bestFor: 'Existing sites and references',
    icon: Link2,
    output: 'Reference-backed draft',
  },
  {
    id: 'ide_file',
    label: 'Paste code',
    description: 'Bring in a component, route, or screen file.',
    bestFor: 'Existing build work',
    icon: Code2,
    output: 'Screen draft',
  },
  {
    id: 'github_pr',
    label: 'Use a pull request',
    description: 'Attach a repository change for review.',
    bestFor: 'Team changes',
    icon: Github,
    output: 'Review card',
  },
  {
    id: 'figma_frame',
    label: 'Figma frame',
    description: 'Bring in a design frame for conversion review.',
    bestFor: 'Design handoff',
    icon: LayoutTemplate,
    output: 'Design-backed draft',
  },
  {
    id: 'figma_file',
    label: 'Figma .fig',
    description: 'Read a local Figma file and keep the original.',
    bestFor: 'Checking a design first',
    icon: FileArchive,
    output: 'Design details',
  },
  // A document is a first-class source, so it gets a card of the same rank as
  // `.fig`. Before this existed the ONLY document upload in Studio was an
  // auxiliary panel under Design ▸ Canvas, and this page's only file input
  // accepted `.json` app bundles — so the front door had no document entrance
  // at all. `output` names what this source really produces today (structure
  // you can read), not an app, because generating from a document is not
  // wired yet and the card must not imply it is.
  {
    id: 'document_file',
    label: 'Word / HWPX',
    description: 'Read a .docx or .hwpx and keep the original.',
    bestFor: 'Specs, guides, manuals',
    icon: FileText,
    output: 'Document details',
  },
]

// `label` has been read from `sources.steps` since the section was
// localized; the `detail` beneath each step was not, and stayed English in
// every language. Both halves now come from the dictionary — the literals
// here are the shape, not the words, and `label`/`detail` are overwritten
// together below so neither can be translated without the other.
/**
 * The two questions a creator actually answers here, in order: do I have
 * anything yet, and if so what is it. `bestFor` already encoded the split —
 * exactly one card reads "starting from an idea" and the rest name an existing
 * artefact — the grid just threw it away and rendered seven peers.
 */
const FROM_IDEA_SOURCES: ImportSource[] = ['assistant_prompt']

const UNIFIED_SOURCE_ACCEPT = [
  DOCUMENT_SOURCE_ACCEPT,
  '.fig',
  '.pen',
  '.tsx',
  '.ts',
  '.jsx',
  '.js',
  '.html',
  '.css',
  '.md',
  '.txt',
  '.json',
].join(',')

const CODE_SOURCE_EXTENSIONS = new Set([
  'tsx',
  'ts',
  'jsx',
  'js',
  'html',
  'css',
  'md',
  'txt',
  'json',
])

const DEFAULT_SOURCE_MESSAGE = 'Choose a source and add it to Library.'

function fileStem(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '').trim()
}

export function groupSourceOptions(
  options: SourceOption[],
): { fromIdea: SourceOption[]; bringExisting: SourceOption[] } {
  return {
    fromIdea: options.filter((o) => FROM_IDEA_SOURCES.includes(o.id)),
    bringExisting: options.filter((o) => !FROM_IDEA_SOURCES.includes(o.id)),
  }
}

const LAUNCH_LOOP = [
  {
    label: 'Add source',
    detail: 'Idea, file, URL, repository, or design',
    icon: UploadCloud,
    tone: 'border-border bg-muted text-foreground',
  },
  {
    label: 'Shape',
    detail: 'cogo prepares the work',
    icon: MonitorPlay,
    tone: 'border-border bg-muted text-foreground',
  },
  {
    label: 'Save',
    detail: 'Keep source and next steps together',
    icon: CheckCircle2,
    tone: 'border-border bg-muted text-foreground',
  },
  {
    label: 'Share',
    detail: 'Open, share, or launch',
    icon: Rocket,
    tone: 'bg-muted text-muted-foreground',
  },
]

function inputTypeForSource(source: ImportSource): CogoXyzInputType {
  if (source === 'ide_file') return 'react'
  if (source === 'url_import') return 'html'
  return 'markdown'
}

function sourceInputWithAgentRequest(
  sourceInput: string,
  agentRequest: string,
  purpose: Exclude<SourcePurpose, 'auto'>,
): string {
  const request = agentRequest.trim()
  return [
    `Source purpose: ${purpose}`,
    '',
    'Studio agent request:',
    request || 'Use the attached source as-is.',
    '',
    'Attached source:',
    sourceInput,
  ].join('\n')
}

function parseGithubInput(
  value: string,
): { repo: string; prNumber: number } | null {
  const trimmed = value.trim()
  const shorthand = trimmed.match(/^([^#]+)#(\d+)$/)
  if (shorthand) {
    return { repo: shorthand[1], prNumber: Number(shorthand[2]) }
  }
  const url = trimmed.match(
    /^https?:\/\/(?:www\.)?github\.com\/([^/]+\/[^/]+)\/pull\/(\d+)(?:[/?#].*)?$/i,
  )
  return url ? { repo: url[1], prNumber: Number(url[2]) } : null
}

function readLibraryRemixDraft(): LibraryRemixDraft | null {
  if (typeof window === 'undefined') return null
  const workItemId = new URLSearchParams(window.location.search).get('remix')
  if (!workItemId) return null
  const item = cogoXyzRegistry.getLibraryItem(workItemId)
  if (!item) return null
  const sourceTitle = item.workItem.title || item.workItem.id
  const target =
    typeof item.workItem.sourceRef.metadata?.target === 'string'
      ? item.workItem.sourceRef.metadata.target
      : 'app'
  return {
    workItemId,
    sourceTitle,
    title: `${sourceTitle} remix`,
    input: [
      `Remix the Library app "${sourceTitle}" into a fresh application-ready asset.`,
      `Original source: ${item.workItem.sourceType} · ${item.workItem.sourceRef.label}.`,
      `Keep the useful structure, improve copy and visual quality, and prepare it for sharing.`,
    ].join('\n'),
    tags: `library-remix,${target}`,
    audience:
      target === 'dashboard' || target === 'admin' ? 'business' : 'creator',
  }
}

export function ImportPage() {
  const [remixDraft] = useState(() => readLibraryRemixDraft())
  const { locale, strings } = useStudioLocale()
  const unifiedFileInputRef = useRef<HTMLInputElement>(null)
  const unifiedComposerRef = useRef<HTMLTextAreaElement>(null)
  const figmaTokenInputRef = useRef<HTMLInputElement>(null)
  const [source, setSource] = useState<ImportSource>('assistant_prompt')
  const [title, setTitle] = useState(
    remixDraft?.title ?? strings.starters.newAppFallbackName,
  )
  const [agentRequest, setAgentRequest] = useState(remixDraft?.input ?? '')
  const [input, setInput] = useState(remixDraft?.input ?? '')
  const [audience, setAudience] = useState(remixDraft?.audience ?? 'creator')
  const [tags, setTags] = useState(remixDraft?.tags ?? '')
  const [figmaToken, setFigmaToken] = useState('')
  const [figmaTokenControl, setFigmaTokenControl] = useState(() =>
    buildFigmaTokenControlFromStoredToken(readStoredFigmaAccessToken()),
  )
  const [figFile, setFigFile] = useState<File | null>(null)
  const [docFile, setDocFile] = useState<File | null>(null)
  const [codeFileName, setCodeFileName] = useState<string | null>(null)
  const [attachedTextInputType, setAttachedTextInputType] =
    useState<CogoXyzInputType | null>(null)
  const [docReadback, setDocReadback] =
    useState<DocumentViewerLoadResult | null>(null)
  const [docReadbackFile, setDocReadbackFile] = useState<File | null>(null)
  const [sourceOperationDetail, setSourceOperationDetail] = useState<
    string | null
  >(null)
  /** Set when a multi-file drop was reduced to its first file. */
  const [dropNotice, setDropNotice] = useState<string | null>(null)
  /** True once a staged front-door drop has chosen this page's source card. */
  const droppedSourceAppliedRef = useRef(false)
  /** Guards the restore-driven card selection so it happens at most once. */
  const documentAutoSelectDoneRef = useRef(false)
  const [openPencilImport, setOpenPencilImport] =
    useState<OpenPencilFigmaSourceBundle | null>(null)
  const [openPencilImportFile, setOpenPencilImportFile] =
    useState<File | null>(null)
  const [importFailure, setImportFailure] = useState<{
    reason: OpenPencilImportFailureReason
    fileName: string
    detail?: string
  } | null>(null)
  const [confirmedFigmaToken, setConfirmedFigmaToken] = useState<string | null>(
    null,
  )
  const [connectingFigma, setConnectingFigma] = useState(false)
  const [readingIntakeFile, setReadingIntakeFile] = useState(false)
  const intakeFileReadRequestRef = useRef(0)
  const documentReadRequestRef = useRef(0)
  const documentReadAbortControllerRef = useRef<AbortController | null>(null)
  const [draggingSource, setDraggingSource] = useState(false)
  const [sourcePurpose, setSourcePurpose] = useState<SourcePurpose>('auto')
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState(
    remixDraft
      ? `Library remix loaded from ${remixDraft.sourceTitle}.`
      : DEFAULT_SOURCE_MESSAGE,
  )
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)

  function replaceDocumentFile(file: File | null) {
    documentReadAbortControllerRef.current?.abort()
    documentReadAbortControllerRef.current = null
    documentReadRequestRef.current += 1
    setDocReadback(null)
    setDocReadbackFile(null)
    setDocFile(file)
  }

  // A design already registered in this browser — from an earlier import in
  // this session, or restored into the workspace. It decides only which
  // sub-line the canvas door shows, never whether the door exists.
  const registeredFigmaSource = useStudioStore((s) => s.openPencilFigmaSource)
  const setOpenPencilImportActivity = useStudioStore(
    (s) => s.setOpenPencilImportActivity,
  )
  // Card copy, page chrome, and the flow steps come from the dictionary so
  // the Sources screen speaks the creator's language; only the icons and ids
  // stay in SOURCE_OPTIONS.
  const sourcesCopy = strings.sources
  const docxCopy = getDocxSourceViewerCopy(locale)
  const figmaTokenStatusMessage =
    figmaTokenControl.status === 'missing'
      ? sourcesCopy.panel.figmaTokenPasteHint
      : figmaTokenControl.message
  // SAME predicate the Home hero composer's attach chip already gates on
  // (`hero-chat-input.tsx`). Both Figma cards below — frame AND `.fig` —
  // dead-end into this page's `openpencil-figma-client.ts` transport in an
  // appliance build (`VITE_OPENPENCIL_IMPORT_API_URL` unset). The chip already
  // hides the word "Figma" for exactly this reason; this page is where a
  // creator following that word ends up, so it must tell the SAME truth
  // instead of re-deriving a second copy of the check.
  // Each intake answers for ITSELF. `.fig` needs the OpenPencil service; a
  // frame needs to reach Figma. Deciding both from the first flag disabled the
  // frame card for a reason that had nothing to do with frames.
  const figmaFileImportAvailable = isOpenPencilFigmaImportAvailable()
  const figmaFrameImportAvailable = isFigmaFrameImportAvailable()
  // This page is the dedicated intake surface (its whole job is "here are the
  // sources you can add"), unlike the hero chip's one-line label — so the
  // Figma cards stay VISIBLE but disabled with a disclosed reason rather than
  // disappearing. Hiding them here would look like Figma was never a source
  // this product supports, which is a worse lie than an honest "not here."
  // Reuses the same typed failure copy `OpenPencilImportFailureNotice` shows
  // after a live attempt (`service-not-configured`, 5 locales) instead of a
  // second hand-written unavailable string.
  const figmaUnavailableCopy = getOpenPencilImportFailureCopy(
    locale,
    'service-not-configured',
  )
  const sourceDoorsCopy = getSourceDoorsCopy(locale)
  const agentIntakeCopy = sourcesCopy.agentIntake
  const sourceIngestCopy = getSourceIngestCopy(locale)
  // SAME condition the `import_figma_frame` handler refuses on
  // (`register-cogo-xyz-tools.ts`: `if (!currentProjectId) throw`). Read here
  // so the card can say WHY before the creator pastes a URL and hands over a
  // Figma access token for a call that cannot land. Kept in step with that
  // handler by a gate derived from its own source
  // (`__tests__/import-page-source-doors.test.tsx`).
  //
  // Deliberately NOT `disabled`: opening a project is a step the creator can
  // take, unlike an appliance build where the network to Figma is closed by
  // construction. Disabling would read as "this product cannot do frames".
  const frameNeedsProject = figmaFrameNeedsOpenProject(currentProjectId)
  const launchLoopCopy = strings.launchLoop
  const launchLoopStepLabels = [
    sourcesCopy.steps.addSource,
    sourcesCopy.steps.shape,
    sourcesCopy.steps.save,
    sourcesCopy.steps.share,
  ]
  const launchLoopStepDetails = [
    launchLoopCopy.addSource,
    launchLoopCopy.shape,
    launchLoopCopy.save,
    launchLoopCopy.share,
  ]
  const launchLoopSteps = LAUNCH_LOOP.map((step, index) => ({
    ...step,
    label: launchLoopStepLabels[index] ?? step.label,
    detail: launchLoopStepDetails[index] ?? step.detail,
  }))
  const activeSource =
    SOURCE_OPTIONS.find((option) => option.id === source) ?? SOURCE_OPTIONS[0]
  const ActiveSourceIcon = activeSource.icon
  const hasSourceInput =
    source === 'figma_file'
      ? Boolean(figFile)
      : source === 'document_file'
        ? Boolean(docFile)
        : Boolean(input.trim())

  // A document already open in this browser (including one restored from the
  // browser store after a reload) must be visible AT THE ENTRANCE, not only
  // deep inside Design ▸ Canvas — otherwise this page reports "no file
  // selected" while a document is loaded.
  const documentSession = useSyncExternalStore(
    subscribeDocxViewerSession,
    getDocxViewerSession,
    getDocxViewerSession,
  )
  const openDocument = documentSession.model?.ok ? documentSession.model : null
  const selectedIntakeFile = docFile ?? figFile
  const sourceGuidance = buildSourceIngestGuidance({
    sourceKind: source,
    selectedPurpose: sourcePurpose,
    hasInput:
      hasSourceInput ||
      (source === 'document_file' && Boolean(openDocument)) ||
      (source === 'figma_file' && Boolean(registeredFigmaSource)),
    running,
    readingFile: readingIntakeFile,
    figmaFileAvailable: figmaFileImportAvailable,
    figmaFrameAvailable: figmaFrameImportAvailable,
    frameNeedsProject,
    figmaTokenReady: figmaTokenControl.canImport,
    hasImportFailure: Boolean(importFailure),
    hasDocumentError: Boolean(docReadback && !docReadback.ok),
    hasOperationError: Boolean(sourceOperationDetail),
    documentReady:
      source === 'document_file' &&
      Boolean(
        docFile
          ? docReadbackFile === docFile && docReadback?.ok
          : docReadback?.ok || openDocument,
      ),
    figmaFileReady:
      source === 'figma_file' &&
      Boolean(
        figFile
          ? openPencilImportFile === figFile &&
              openPencilImport?.fileName === figFile.name &&
              openPencilImport.readback &&
              openPencilImport.sourceArtifactRef
          : (openPencilImport?.readback &&
              openPencilImport.sourceArtifactRef) ||
              (registeredFigmaSource?.readback &&
                registeredFigmaSource.sourceArtifactRef),
      ),
    // Figma-frame imports do not produce an OpenPencil file readback on this
    // surface. A retained .fig bundle therefore cannot authorize a new frame.
    figmaFrameReady: false,
  })

  // A file dropped on the Studio front door is staged in module scope and
  // applied HERE, on the page that owns the pickers. Read non-destructively
  // and cleared after applying, so React StrictMode's double effect cannot
  // hand the file to one invocation and nothing to the other.
  //
  // Declared BEFORE the restore effect below so a drop always wins the card
  // selection: effects run in declaration order, and this one is synchronous
  // while the restore resolves a promise.
  useEffect(() => {
    const staged = readPendingSourceDrop()
    if (!staged) return
    clearPendingSourceDrop()
    droppedSourceAppliedRef.current = true
    setDropNotice(
      staged.droppedCount > 1
        ? getSourceDropCopy(locale).onlyFirstFile(
            staged.file.name,
            staged.droppedCount,
          )
        : null,
    )
    if (staged.kind === 'document') {
      setSource('document_file')
      replaceDocumentFile(staged.file)
      setMessage(`${staged.file.name} · ${sourcesCopy.panel.readDocument}`)
      return
    }
    // `.fig` runs through the SAME gate the picker uses — a dropped file must
    // not bypass a check a chosen file cannot.
    const gate = checkOpenPencilSourceFile({
      name: staged.file.name,
      size: staged.file.size,
    })
    setSource('figma_file')
    if (!gate.ok) {
      setFigFile(null)
      setImportFailure({ reason: gate.reason, fileName: staged.file.name })
      setMessage(getOpenPencilImportFailureCopy(locale, gate.reason).title)
      return
    }
    setFigFile(staged.file)
    setMessage(`${staged.file.name} · ${sourcesCopy.panel.readDesign}`)
    // Locale is read for failure copy only; re-running on a locale change
    // would re-apply a drop that was already consumed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Read back a stored document once per page load. The one-shot guard lives
  // in the state module, so doing this here AND in the viewer costs one read.
  //
  // Measured 2026-07-30 BEFORE this existed: a reload of `/studio/import`
  // restored the document into the shared session, but the page came back with
  // its card selection reset to "Describe it", so the readback panel was never
  // rendered and the entrance looked empty while a document was open. Reload
  // survival that the interface does not show is not survival.
  useEffect(() => {
    void restoreDocumentSource()
  }, [])

  // …and select its card when it arrives.
  //
  // Driven by the SUBSCRIBED session rather than by the restore promise's
  // `.then`. Under React StrictMode the mount effect runs, is cleaned up, and
  // runs again; the second `restoreDocumentSource()` returns null immediately
  // (the one-shot guard already fired) and its continuation therefore ran
  // BEFORE the first call's IndexedDB read had produced a model, while the
  // first continuation had been cancelled by the cleanup. Net effect measured
  // 2026-07-30: after a reload the document was restored but the page still
  // showed "Describe it" selected and no readback. Reacting to the session
  // value has no such ordering to lose.
  useEffect(() => {
    if (documentAutoSelectDoneRef.current) return
    if (!openDocument) return
    documentAutoSelectDoneRef.current = true
    // A drop the creator JUST made, or a Library remix they arrived with,
    // outranks a document from a previous session.
    if (droppedSourceAppliedRef.current || remixDraft) return
    setSource((current) =>
      current === 'assistant_prompt' ? 'document_file' : current,
    )
  }, [openDocument, remixDraft])

  function updateFigmaTokenInput(value: string) {
    setSourceOperationDetail(null)
    setFigmaToken(value)
    setFigmaTokenControl(buildFigmaTokenControlFromStoredToken(value))
    setConfirmedFigmaToken(null)
  }

  function updateUnifiedRequest(value: string) {
    setSourceOperationDetail(null)
    setAgentRequest(value)
    // An attached resource remains the active source while the creator adds
    // intent around it. With no attachment, a standalone URL can reuse the
    // existing URL handler and every other request reuses the brief handler.
    if (docFile || figFile || codeFileName) return
    setInput(value)
    setSource(detectStudioSourceKind(value))
  }

  async function selectUnifiedFile(file: File | null) {
    if (!file) return
    const requestId = ++intakeFileReadRequestRef.current
    setReadingIntakeFile(false)
    setSourceOperationDetail(null)
    const extension = fileExtensionOf(file.name)
      .replace(/^\./, '')
      .toLowerCase()

    if (documentFormatForFileName(file.name)) {
      setSource('document_file')
      replaceDocumentFile(file)
      setFigFile(null)
      setCodeFileName(null)
      setAttachedTextInputType(null)
      setDropNotice(null)
      setImportFailure(null)
      if (title === strings.starters.newAppFallbackName) {
        setTitle(fileStem(file.name) || title)
      }
      setMessage(`${file.name} · ${sourcesCopy.panel.readDocument}`)
      return
    }

    if (extension === 'fig' || extension === 'pen') {
      setSource('figma_file')
      setOpenPencilImport(null)
      setOpenPencilImportFile(null)
      replaceDocumentFile(null)
      setCodeFileName(null)
      setAttachedTextInputType(null)
      if (!figmaFileImportAvailable) {
        setFigFile(null)
        setImportFailure({
          reason: 'service-not-configured',
          fileName: file.name,
        })
        setMessage(figmaUnavailableCopy.title)
        setAdvancedOpen(true)
        return
      }
      const gate = checkOpenPencilSourceFile({
        name: file.name,
        size: file.size,
      })
      if (!gate.ok) {
        setFigFile(null)
        setImportFailure({ reason: gate.reason, fileName: file.name })
        setMessage(getOpenPencilImportFailureCopy(locale, gate.reason).title)
        setAdvancedOpen(true)
        return
      }
      setImportFailure(null)
      setFigFile(file)
      if (title === strings.starters.newAppFallbackName) {
        setTitle(fileStem(file.name) || title)
      }
      setMessage(`${file.name} · ${sourcesCopy.panel.readDesign}`)
      return
    }

    if (CODE_SOURCE_EXTENSIONS.has(extension)) {
      setReadingIntakeFile(true)
      try {
        const text = await file.text()
        if (requestId !== intakeFileReadRequestRef.current) return
        setSource('ide_file')
        replaceDocumentFile(null)
        setFigFile(null)
        setCodeFileName(file.name)
        setAttachedTextInputType(
          extension === 'md' || extension === 'txt' ? 'markdown' : 'react',
        )
        setImportFailure(null)
        setInput(text)
        if (title === strings.starters.newAppFallbackName) {
          setTitle(fileStem(file.name) || title)
        }
        setMessage(`${file.name} · ${sourcesCopy.cards.ide_file.label}`)
      } catch {
        if (requestId === intakeFileReadRequestRef.current) {
          setMessage(agentIntakeCopy.unsupportedFile)
        }
      } finally {
        if (requestId === intakeFileReadRequestRef.current) {
          setReadingIntakeFile(false)
        }
      }
      return
    }

    setMessage(agentIntakeCopy.unsupportedFile)
  }

  function dropUnifiedSource(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setDraggingSource(false)
    const files = [...event.dataTransfer.files]
    const first = files[0]
    if (!first) return
    setDropNotice(
      files.length > 1
        ? getSourceDropCopy(locale).onlyFirstFile(first.name, files.length)
        : null,
    )
    void selectUnifiedFile(first)
  }

  function clearUnifiedFile() {
    intakeFileReadRequestRef.current += 1
    setReadingIntakeFile(false)
    setSourceOperationDetail(null)
    replaceDocumentFile(null)
    setFigFile(null)
    setCodeFileName(null)
    setAttachedTextInputType(null)
    setImportFailure(null)
    setInput(agentRequest)
    setSource(detectStudioSourceKind(agentRequest))
    setMessage(DEFAULT_SOURCE_MESSAGE)
    unifiedComposerRef.current?.focus()
  }

  function overrideUnifiedSource(nextSource: ImportSource) {
    intakeFileReadRequestRef.current += 1
    setReadingIntakeFile(false)
    setSourceOperationDetail(null)
    const attachedSource: ImportSource | null = docFile
      ? 'document_file'
      : figFile
        ? 'figma_file'
        : codeFileName
          ? 'ide_file'
          : null
    if (attachedSource && attachedSource !== nextSource) {
      replaceDocumentFile(null)
      setFigFile(null)
      setCodeFileName(null)
      setAttachedTextInputType(null)
      setImportFailure(null)
      setDropNotice(null)
      setInput(agentRequest)
      setMessage(DEFAULT_SOURCE_MESSAGE)
    }
    setSource(nextSource)
  }

  function recoverSourceIngest() {
    switch (sourceGuidance.recoveryAction) {
      case 'choose-file':
        unifiedFileInputRef.current?.click()
        return
      case 'connect-figma':
        setAdvancedOpen(true)
        window.setTimeout(() => figmaTokenInputRef.current?.focus(), 0)
        return
      case 'open-project':
        setStudioRoute('projects')
        pushStudioRoute('projects')
        return
      case 'retry':
        void createPreview()
        return
      case 'sign-in':
        window.location.assign('/login')
        return
      default:
        return
    }
  }

  async function confirmFigmaToken(): Promise<FigmaTokenControl> {
    const token = figmaToken.trim() || readStoredFigmaAccessToken() || ''
    if (!token) {
      const missing = buildFigmaTokenControlFromStoredToken(undefined)
      setFigmaTokenControl(missing)
      return missing
    }

    setConnectingFigma(true)
    setFigmaTokenControl({
      status: 'needs_confirmation',
      action: 'confirm_token',
      canImport: false,
      message: 'Checking Figma token...',
      statusCode: null,
    })
    try {
      const result = await mcpBus.execute({
        tool: 'cogo.studio.validate_figma_token',
        params: { figmaAccessToken: token },
      })
      if (!result.success) {
        const failed = buildFigmaTokenControlFromImportError(
          result.error,
          token,
        ) ?? {
          status: 'network_error',
          action: 'retry_later',
          canImport: false,
          message: result.error ?? 'Could not check Figma token.',
          statusCode: null,
          tokenLabel: undefined,
        }
        setFigmaTokenControl(failed)
        return failed
      }

      const validation = result.data as FigmaTokenValidationResult
      const control = validation.control
      setFigmaTokenControl(control)
      if (control.canImport) {
        saveStoredFigmaAccessToken(token)
        setConfirmedFigmaToken(token)
        setMessage(control.message)
      } else if (control.status === 'expired' || control.status === 'invalid') {
        clearStoredFigmaAccessToken()
        setConfirmedFigmaToken(null)
      }
      return control
    } finally {
      setConnectingFigma(false)
    }
  }

  async function createPreview() {
    const trimmedTitle = title.trim() || strings.starters.newAppFallbackName
    const trimmedInput = input.trim()
    const resolvedPurpose = sourceGuidance.resolvedPurpose
    const purposeTag = sourcePurposeTag(resolvedPurpose)
    const selectedFigFile = figFile
    const selectedDocFile = docFile
    // Refused HERE, before the URL is parsed and before a Figma token is asked
    // for, because the handler refuses this case outright — running would
    // spend the creator's token on a call that throws and report it as a
    // generic import failure. First check, so what blocks is named honestly
    // rather than mistaken for a bad URL.
    if (source === 'figma_frame' && frameNeedsProject) {
      setMessage(sourceDoorsCopy.frameNeedsProjectStatus)
      return
    }
    if (
      source !== 'figma_file' &&
      source !== 'document_file' &&
      !trimmedInput
    )
      return
    if (source === 'figma_file' && !selectedFigFile) {
      setMessage(sourcesCopy.help.figma_file.title)
      return
    }
    if (source === 'document_file' && !selectedDocFile) {
      setMessage(sourcesCopy.help.document_file.title)
      return
    }
    if (source === 'url_import' && registeredFigmaSource) {
      // The generation contract fails closed while a design source owns the
      // active source authority. Explain that conflict before dispatch instead
      // of leaking the internal admission error from the MCP boundary.
      setMessage(agentIntakeCopy.activeDesignConflict)
      return
    }
    if (source === 'github_pr' && resolvedPurpose !== 'requirements') {
      setMessage(sourceIngestCopy.githubPurposeUnsupported)
      return
    }

    setSourceOperationDetail(null)
    const documentReadAbortController =
      source === 'document_file' ? new AbortController() : null
    const documentReadRequestId =
      source === 'document_file' ? ++documentReadRequestRef.current : null
    if (source === 'document_file') {
      documentReadAbortControllerRef.current?.abort()
      documentReadAbortControllerRef.current = documentReadAbortController
      setDocReadback(null)
      setDocReadbackFile(null)
    }
    if (source === 'figma_file') setImportFailure(null)
    setRunning(true)
    setMessage(docxCopy.header.parsing)
    try {
      if (source === 'document_file') {
        const file = selectedDocFile
        if (!file) return
        setMessage(docxCopy.header.parsing)
        // Same in-browser extractor, same shared session and same browser
        // store the Design ▸ Canvas viewer uses. One document, one place —
        // opening it here means it is already open there.
        let result: DocumentSourceOpenResult
        try {
          const bytes = new Uint8Array(await file.arrayBuffer())
          if (documentReadRequestId !== documentReadRequestRef.current) return
          result = await openDocumentSource(bytes, file.name, {
            projectId: currentProjectId,
            contentType: file.type,
            signal: documentReadAbortController?.signal,
            isCurrentSelection: () =>
              documentReadRequestId === documentReadRequestRef.current,
          })
          if (documentReadRequestId !== documentReadRequestRef.current) return
        } catch {
          if (documentReadRequestId !== documentReadRequestRef.current) return
          // Reading bytes off disk failed (file moved, permission revoked).
          // Surfaced as a readback error rather than leaving "Reading..." up.
          result = {
            ok: false,
            format: documentFormatForFileName(file.name),
            fileName: file.name,
            byteSize: file.size ?? 0,
            errorCode: 'read-failed',
            message:
              'The file could not be read from disk. Try choosing it again.',
          }
          setDocReadbackFile(file)
          setDocReadback(result)
          setMessage(docxCopy.error.couldNotRead(file.name))
          return
        }
        setDocReadbackFile(file)
        setDocReadback(result)
        setMessage(
          result.ok
            ? result.persist?.state === 'success' && result.persist.readbackAccepted
              ? `${sourcesCopy.after.steps.documentRead} · ${result.blocks.length} ${strings.report.conversionProvenance.blocksLabel}. ${sourcesCopy.after.steps.sourceSaved}`
              : `${sourcesCopy.after.steps.documentRead} · ${result.blocks.length} ${strings.report.conversionProvenance.blocksLabel}. ${sourcesCopy.after.steps.keptInBrowser}`
            : docxCopy.error.couldNotRead(result.fileName),
        )
        return
      }

      if (source === 'figma_file') {
        const file = selectedFigFile
        if (!file) {
          setMessage(sourcesCopy.help.figma_file.title)
          return
        }
        // UX audit finding 1 — the failure must be presented HERE, at the
        // moment it happens, instead of navigating the creator to a canvas
        // that cannot explain it.
        const startedAt = Date.now()
        setImportFailure(null)
        setOpenPencilImportActivity({
          phase: 'importing',
          fileName: file.name,
          sourceName: trimmedTitle,
          statusLabel: 'Reading the design',
          startedAt,
          updatedAt: startedAt,
        })
        let failureReason: OpenPencilImportFailureReason | null = null
        let failureDetail: string | null = null
        const bundle = await importOpenPencilFigmaFile({
          file,
          sourceName: trimmedTitle,
          workspaceId: currentProjectId ?? 'workspace_local',
          onStatus: (status) => {
            setMessage(`${status}...`)
            setOpenPencilImportActivity({
              phase: 'importing',
              fileName: file.name,
              sourceName: trimmedTitle,
              statusLabel: status,
              startedAt,
              updatedAt: Date.now(),
            })
          },
          onFailure: (failure) => {
            failureReason = failure.reason
            failureDetail = failure.detail
          },
        })
        setOpenPencilImport(bundle)
        setOpenPencilImportFile(file)
        // Reading a source and admitting it for Coogo generation are separate
        // decisions. A readable .fig remains available to the browser editor
        // even while source-mapping or semantic-review gates are unresolved.
        const readable = bundle.readback !== null && Boolean(bundle.sourceArtifactRef)
        const importFailed = !readable
        const result = await mcpBus.execute({
          tool: 'cogo.studio.import_openpencil_figma_source',
          params: {
            sourceBundle: bundle,
            openInSourceSurface: !importFailed,
          },
        })
        if (!result.success || importFailed) {
          const reason: OpenPencilImportFailureReason =
            failureReason ??
            (importFailed
              ? classifyOpenPencilImportFailureText(
                  bundle.blockedEvidence?.reason,
                )
              : 'unknown')
          const detail =
            failureDetail ??
            (result.success ? bundle.blockedEvidence?.reason : result.error)
          setImportFailure({
            reason,
            fileName: file.name,
            ...(detail ? { detail } : {}),
          })
          setOpenPencilImportActivity({
            phase: 'blocked',
            fileName: file.name,
            sourceName: trimmedTitle,
            reason,
            title: getOpenPencilImportFailureCopy(locale, reason).title,
            ...(detail ? { detail } : {}),
            startedAt,
            updatedAt: Date.now(),
          })
          setMessage(getOpenPencilImportFailureCopy(locale, reason).title)
          return
        }
        setOpenPencilImportActivity(null)
        setMessage(
          `Design read: ${bundle.sourceFacts?.documentProfile.pages ?? 0} pages, ${bundle.sourceFacts?.documentProfile.totalNodes ?? 0} layers.`,
        )
        return
      }

      const figmaRef =
        source === 'figma_frame' ? parseFigmaFrameReference(trimmedInput) : null
      const effectiveFigmaToken =
        figmaToken.trim() || readStoredFigmaAccessToken()
      if (source === 'figma_frame' && !figmaRef) {
        setMessage(
          'Paste a Figma design/file URL with node-id, or enter file key and node id.',
        )
        return
      }
      if (source === 'figma_frame' && !effectiveFigmaToken) {
        setFigmaTokenControl(buildFigmaTokenControlFromStoredToken(undefined))
        setMessage(sourcesCopy.panel.figmaTokenPasteHint)
        return
      }
      if (source === 'figma_frame') {
        const control =
          figmaTokenControl.canImport &&
          confirmedFigmaToken === effectiveFigmaToken
            ? figmaTokenControl
            : await confirmFigmaToken()
        if (!control.canImport) {
          setMessage(control.message)
          return
        }
      }
      const common = {
        workspaceId: 'workspace_local',
        title: trimmedTitle,
        createdBy: 'studio-import-page',
      }
      const result =
        source === 'github_pr'
          ? await mcpBus.execute({
              tool: 'cogo.studio.import_github_pr_preview',
              params: {
                ...common,
                ...(parseGithubInput(trimmedInput) ?? {
                  repo: trimmedInput,
                  prNumber: 1,
                }),
              },
            })
          : source === 'figma_frame'
            ? await mcpBus.execute({
                tool: 'cogo.studio.import_figma_frame',
                params: {
                  ...common,
                  figmaFileId: figmaRef?.figmaFileId,
                  figmaNodeId: figmaRef?.figmaNodeId,
                  figmaAccessToken: effectiveFigmaToken,
                  ...(figmaRef?.figmaUrl
                    ? { figmaUrl: figmaRef.figmaUrl }
                    : {}),
                  createPreview: true,
                  audience,
                  tags: tags
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean)
                    .concat(purposeTag),
                },
              })
            : source === 'url_import'
              ? await mcpBus.execute({
                  tool: 'cogo.studio.import_cogo_asset',
                  params: {
                    ...common,
                    importKind: 'url',
                    input: trimmedInput,
                    sourceUrl: trimmedInput,
                    target: audience === 'business' ? 'dashboard' : 'app',
                    audience,
                    tags: tags
                      .split(',')
                      .map((tag) => tag.trim())
                      .filter(Boolean)
                      .concat(purposeTag),
                    visibility: 'unlisted',
                    publishGallery: false,
                  },
                })
            : await mcpBus.execute({
                tool: 'cogo.studio.create_cogo_app',
                params: {
                  ...common,
                  sourceType: remixDraft
                    ? 'template_remix'
                    : (source as CogoXyzSourceType),
                  inputType:
                    attachedTextInputType ?? inputTypeForSource(source),
                  input: codeFileName
                    ? sourceInputWithAgentRequest(
                        trimmedInput,
                        agentRequest,
                        resolvedPurpose,
                      )
                    : [
                        `Source purpose: ${resolvedPurpose}`,
                        '',
                        trimmedInput,
                      ].join('\n'),
                  target: audience === 'business' ? 'dashboard' : 'app',
                },
              })

      if (!result.success) {
        if (source === 'figma_frame') {
          const tokenControl = buildFigmaTokenControlFromImportError(
            result.error,
            effectiveFigmaToken,
          )
          if (tokenControl) {
            setFigmaTokenControl(tokenControl)
            if (
              tokenControl.status === 'expired' ||
              tokenControl.status === 'invalid'
            ) {
              clearStoredFigmaAccessToken()
              setConfirmedFigmaToken(null)
            }
            setMessage(tokenControl.message)
            return
          }
        }
        const explained = explainSourceOperationError(
          result.error ?? 'Could not prepare this item.',
        )
        setSourceOperationDetail(explained.technicalDetail)
        setMessage(sourceIngestCopy.operationError[explained.messageKey])
        return
      }

      // A successful tool dispatch is not proof that Library has something
      // it can render. The creator-facing prompt/code path must use the same
      // list authority Library renders before leaving Sources; otherwise an
      // individually readable WorkItem can still land on an empty Library and
      // look like completed work. Keep the request in place so retry remains
      // possible.
      if (source !== 'github_pr' && source !== 'figma_frame') {
        const workItemId =
          result.data &&
          typeof result.data === 'object' &&
          typeof (result.data as { workItemId?: unknown }).workItemId ===
            'string'
            ? (result.data as { workItemId: string }).workItemId
            : null
        const visibleLibraryItem = workItemId
          ? cogoXyzRegistry
              .listLibraryItems({ includeArchived: true })
              .items.some((item) => item.workItem.id === workItemId)
          : false
        if (!visibleLibraryItem) {
          setMessage(agentIntakeCopy.librarySaveMissing)
          return
        }

        // Prompt generation can activate a newly created server project before
        // create_cogo_app persists the Registry snapshot. Library reloads that
        // project's settings on mount, so an in-memory item is not sufficient
        // evidence that the destination page will render it. Exercise the same
        // reload boundary before navigating and keep the request on Sources if
        // persistence was swallowed or the saved snapshot is stale.
        if (useStudioStore.getState().projectSource === 'server') {
          try {
            const loaded = await loadCogoXyzRegistryFromCurrentProject()
            const survivesLibraryReload =
              loaded &&
              cogoXyzRegistry
                .listLibraryItems({ includeArchived: true })
                .items.some((item) => item.workItem.id === workItemId)
            if (!survivesLibraryReload) {
              setMessage(agentIntakeCopy.librarySaveMissing)
              return
            }
          } catch {
            setMessage(agentIntakeCopy.librarySaveMissing)
            return
          }
        }
      }

      // A frame import lands as a REBUILD, and the creator's next move is to
      // compare it against the Figma file it came from. The generic "source is
      // saved" is true of every other door and says nothing about that, so the
      // frame door names its own result here.
      setMessage(
        source === 'figma_frame'
          ? sourceDoorsCopy.frameImportedStatus
          : sourcesCopy.after.steps.sourceSaved,
      )
      setStudioRoute('library')
      pushStudioRoute('library')
    } catch (error) {
      const explained = explainSourceOperationError(error)
      setSourceOperationDetail(explained.technicalDetail)
      setMessage(sourceIngestCopy.operationError[explained.messageKey])
    } finally {
      if (
        documentReadAbortControllerRef.current ===
        documentReadAbortController
      ) {
        documentReadAbortControllerRef.current = null
      }
      setRunning(false)
    }
  }

  function forgetFigmaToken() {
    clearStoredFigmaAccessToken()
    setFigmaToken('')
    setConfirmedFigmaToken(null)
    setFigmaTokenControl(buildFigmaTokenControlFromStoredToken(undefined))
    setMessage('Figma token removed.')
  }

  return (
    <DashboardContainer
      className="p-6 space-y-6 max-w-7xl"
      data-testid="studio-import-page"
    >
      <PageHeader
        route="import"
        title={sourcesCopy.title}
        description={sourcesCopy.subtitle}
      />

      <section
        className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
        aria-labelledby="studio-agent-source-title"
        data-testid="studio-agent-source-intake"
      >
        <div className="border-b border-border bg-gradient-to-br from-background via-background to-muted/40 p-5 sm:p-7">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-foreground text-background">
              <Sparkles className="h-3.5 w-3.5" />
            </span>
            {agentIntakeCopy.eyebrow}
          </div>
          <h2
            id="studio-agent-source-title"
            className="mt-4 text-xl font-semibold tracking-tight text-foreground sm:text-2xl"
          >
            {agentIntakeCopy.title}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
            {agentIntakeCopy.description}
          </p>

          <div
            className={`mt-5 rounded-2xl border bg-background p-3 shadow-sm transition-colors focus-within:border-foreground/30 focus-within:ring-2 focus-within:ring-border/60 ${draggingSource ? 'border-foreground ring-2 ring-border/60' : 'border-border'}`}
            onDragEnter={(event) => {
              event.preventDefault()
              setDraggingSource(true)
            }}
            onDragOver={(event) => {
              event.preventDefault()
              event.dataTransfer.dropEffect = 'copy'
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                setDraggingSource(false)
              }
            }}
            onDrop={dropUnifiedSource}
            data-testid="studio-agent-source-composer"
          >
            <textarea
              ref={unifiedComposerRef}
              value={agentRequest}
              onChange={(event) => updateUnifiedRequest(event.target.value)}
              aria-label={agentIntakeCopy.title}
              placeholder={agentIntakeCopy.placeholder}
              data-testid="studio-agent-source-request"
              className="min-h-28 w-full resize-y bg-transparent px-1 py-1 text-sm leading-6 text-foreground outline-none placeholder:text-muted-foreground/60"
            />
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <input
                ref={unifiedFileInputRef}
                type="file"
                accept={UNIFIED_SOURCE_ACCEPT}
                className="sr-only"
                data-testid="studio-agent-source-file-input"
                onChange={(event) => {
                  const picked = event.target.files?.[0] ?? null
                  event.target.value = ''
                  void selectUnifiedFile(picked)
                }}
              />
              <button
                type="button"
                onClick={() => unifiedFileInputRef.current?.click()}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                data-testid="studio-agent-source-attach"
              >
                <UploadCloud className="h-3.5 w-3.5" />
                {agentIntakeCopy.attach}
              </button>
              <button
                type="button"
                onClick={() => {
                  unifiedComposerRef.current?.focus()
                  setMessage(agentIntakeCopy.urlHint)
                }}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                data-testid="studio-agent-source-url"
              >
                <Link2 className="h-3.5 w-3.5" />
                {agentIntakeCopy.addUrl}
              </button>
              {(selectedIntakeFile || codeFileName) && (
                <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted py-1 pl-3 pr-1 text-xs font-medium text-muted-foreground">
                  <span className="truncate">
                    {selectedIntakeFile?.name ?? codeFileName}
                  </span>
                  <button
                    type="button"
                    onClick={clearUnifiedFile}
                    aria-label={`Remove ${selectedIntakeFile?.name ?? codeFileName}`}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-background hover:text-foreground"
                    data-testid="studio-agent-source-remove-file"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              <button
                type="button"
                onClick={createPreview}
                disabled={running || readingIntakeFile || !hasSourceInput}
                data-testid="studio-import-create-preview"
                className="ml-auto inline-flex h-10 items-center gap-2 rounded-lg bg-foreground px-4 text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {running || readingIntakeFile ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {source === 'figma_file'
                  ? sourcesCopy.panel.readDesign
                  : source === 'document_file'
                    ? sourcesCopy.panel.readDocument
                    : agentIntakeCopy.submit}
              </button>
            </div>
            {dropNotice ? (
              <p className="mt-2 text-xs text-muted-foreground" role="status">
                {dropNotice}
              </p>
            ) : null}
          </div>
        </div>

        <SourceIngestGuidancePanel
          locale={locale}
          guidance={sourceGuidance}
          selectedPurpose={sourcePurpose}
          statusMessage={
            message === DEFAULT_SOURCE_MESSAGE ? null : message
          }
          technicalDetail={
            (docReadback && !docReadback.ok ? docReadback.message : null) ??
            sourceOperationDetail
          }
          onPurposeChange={setSourcePurpose}
          onRecovery={recoverSourceIngest}
        />
      </section>

      <details
        open={advancedOpen}
        onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}
        className="group rounded-2xl border border-border bg-card shadow-sm"
        data-testid="studio-import-advanced"
      >
        <summary className="flex cursor-pointer list-none items-center gap-3 px-5 py-4 marker:hidden">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <Code2 className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-foreground">
              {agentIntakeCopy.advanced}
            </span>
            <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
              {agentIntakeCopy.advancedSummary}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-6 border-t border-border p-5 sm:p-6">

      {/* Measured 2026-07-30: 112 px (9.9%) of a 1440x900 first screen and
          382 px (39.7%) at 390 px wide, with nothing to click inside it. It
          folds now, and the choice is remembered. */}
      <GuidanceDisclosure
        id="import-launch-loop"
        title={sourcesCopy.howItWorks}
        summary={launchLoopSteps.map((step) => step.label).join(' → ')}
        className="shadow-sm"
        testId="studio-import-launch-loop"
      >
        {/* Stays a named <section> (an ARIA region), which is how the
            landmark survived being moved inside the disclosure. */}
        <section
          className="grid gap-3 md:grid-cols-4"
          aria-label={sourcesCopy.flowAriaLabel}
        >
          {launchLoopSteps.map((step, index) => {
            const Icon = step.icon
            return (
              <div
                key={step.label}
                className="flex min-h-20 items-center gap-3 rounded-xl bg-background px-3 py-3"
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${step.tone}`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {step.label}
                    </span>
                    {index < LAUNCH_LOOP.length - 1 && (
                      <ArrowRight className="hidden h-3.5 w-3.5 text-muted-foreground md:block" />
                    )}
                  </div>
                  <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                    {step.detail}
                  </p>
                </div>
              </div>
            )
          })}
        </section>
      </GuidanceDisclosure>

      <section
        className="rounded-xl border border-border bg-background p-4"
        aria-label={strings.a11y.importSources}
        data-testid="studio-import-source-override"
      >
        <label className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_280px] sm:items-center">
          <span>
            <span className="block text-sm font-semibold text-foreground">
              {sourceIngestCopy.typeOverride}
            </span>
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">
              {sourceIngestCopy.typeOverrideHelp}
            </span>
          </span>
          <select
            value={source}
            onChange={(event) => {
              overrideUnifiedSource(event.target.value as ImportSource)
            }}
            className="h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-foreground/40 focus:ring-2 focus:ring-border/60"
            data-testid="studio-import-source-type-override"
          >
            {SOURCE_OPTIONS.map((option) => {
              const resultLabel =
                option.id === 'figma_frame'
                  ? sourceDoorsCopy.frameProducesChip
                  : option.id === 'figma_file'
                    ? sourceDoorsCopy.fileProducesChip
                    : null
              const unavailable =
                (option.id === 'figma_frame' && !figmaFrameImportAvailable) ||
                (option.id === 'figma_file' && !figmaFileImportAvailable)

              return (
                <option
                  key={option.id}
                  value={option.id}
                  disabled={unavailable}
                  data-testid={`studio-import-source-${option.id}`}
                >
                  {sourcesCopy.cards[option.id]?.label ?? option.label}
                  {resultLabel ? ` — ${resultLabel}` : ''}
                </option>
              )
            })}
          </select>
        </label>
        {!figmaFrameImportAvailable || !figmaFileImportAvailable ? (
          <div className="mt-3 space-y-1 text-xs text-muted-foreground">
            {!figmaFrameImportAvailable ? (
              <p data-testid="studio-import-source-figma_frame-unavailable">
                {sourcesCopy.cards.figma_frame.label}: {figmaUnavailableCopy.title}
              </p>
            ) : null}
            {!figmaFileImportAvailable ? (
              <p data-testid="studio-import-source-figma_file-unavailable">
                {sourcesCopy.cards.figma_file.label}: {figmaUnavailableCopy.title}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      {remixDraft && (
        <section
          className="rounded-xl border border-border bg-muted/35 px-4 py-3"
          data-testid="studio-import-remix-source"
        >
          <div className="text-sm font-semibold text-foreground">
            Library remix
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {remixDraft.sourceTitle} is loaded as the source asset for this new
            Library output.
          </p>
        </section>
      )}

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {sourcesCopy.form.appName}
              </span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                data-testid="studio-import-title"
                className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/40 focus:ring-2 focus:ring-border/60"
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {sourcesCopy.form.audience}
              </span>
              <select
                value={audience}
                onChange={(event) => setAudience(event.target.value)}
                data-testid="studio-import-audience"
                className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-foreground/40 focus:ring-2 focus:ring-border/60"
              >
                <option value="creator">{sourcesCopy.form.audienceCreator}</option>
                <option value="business">{sourcesCopy.form.audienceBusiness}</option>
                <option value="developer">{sourcesCopy.form.audienceDeveloper}</option>
              </select>
            </label>
          </div>

          <label className="mt-4 block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {`${sourcesCopy.refLabels[source]} ${sourcesCopy.form.sourceFieldSuffix}`}
            </span>
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={
                source === 'assistant_prompt'
                  ? sourcesCopy.form.briefPlaceholder
                  : sourcesCopy.help[source].example
              }
              data-testid="studio-import-source-input"
              className="min-h-56 w-full rounded-xl border border-border bg-background px-3 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/40 focus:ring-2 focus:ring-border/60"
            />
            <span
              data-testid="studio-import-source-help"
              className="block text-xs leading-5 text-muted-foreground"
            >
              {sourcesCopy.help[source].title}{' '}
              {sourcesCopy.help[source].detail}
            </span>
          </label>

          {source === 'figma_frame' && frameNeedsProject && (
            <div
              className="mt-4 rounded-xl border border-border bg-muted/30 px-4 py-3 text-xs leading-5 text-muted-foreground"
              role="note"
              data-testid="studio-import-figma-frame-project-notice"
            >
              {sourceDoorsCopy.frameNeedsProjectNotice}
            </div>
          )}

          {/* The mechanism, disclosed BEFORE the creator hands over a token
              and a URL. Unconditional on the frame door: this is not a fault
              to be cleared like the missing-project notice above, it is what
              this door does every time. Reading it here is what lets a creator
              who later compares the result against Figma know that the
              differences are the conversion. */}
          {source === 'figma_frame' && (
            <div
              className="mt-4 rounded-xl border border-border bg-muted/30 px-4 py-3 text-xs leading-5 text-muted-foreground"
              role="note"
              data-testid="studio-import-figma-frame-conversion-notice"
            >
              {sourceDoorsCopy.frameConversionNotice}
              {/* The way out is offered only where it EXISTS. In the cloud
                  build `VITE_OPENPENCIL_IMPORT_API_URL` ships empty, which
                  disables the `.fig` card while leaving this one live — so the
                  frame door is the only Figma door there, and naming `.fig`
                  would send the creator to a card they cannot click. Pointing
                  at a shut door on the panel whose job is to stop misleading
                  them would reintroduce the defect in a new place. */}
              {figmaFileImportAvailable && (
                <> {sourceDoorsCopy.frameConversionAlternative}</>
              )}
            </div>
          )}

          {source === 'figma_frame' && (
            <div
              className="mt-4 rounded-xl border border-border bg-background p-4"
              data-testid="studio-import-figma-token-panel"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <KeyRound className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {sourcesCopy.panel.figmaTokenLabel}
                    </div>
                    <div
                      className="text-xs text-muted-foreground"
                      data-testid="studio-import-figma-token-status"
                    >
                      {figmaTokenControl.tokenLabel
                        ? `${figmaTokenControl.tokenLabel} · ${figmaTokenStatusMessage}`
                        : figmaTokenStatusMessage}
                    </div>
                  </div>
                </div>
                <input
                  ref={figmaTokenInputRef}
                  type="password"
                  value={figmaToken}
                  onChange={(event) =>
                    updateFigmaTokenInput(event.target.value)
                  }
                  placeholder="Paste Figma access token"
                  data-testid="studio-import-figma-token-input"
                  className="h-9 min-w-[240px] flex-1 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/40 focus:ring-2 focus:ring-border/60"
                />
                <button
                  type="button"
                  onClick={confirmFigmaToken}
                  disabled={
                    connectingFigma ||
                    !(figmaToken.trim() || readStoredFigmaAccessToken())
                  }
                  data-testid="studio-import-figma-token-connect"
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {connectingFigma ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  확인
                </button>
                <button
                  type="button"
                  onClick={forgetFigmaToken}
                  data-testid="studio-import-figma-token-forget"
                  className="inline-flex h-9 items-center rounded-lg border border-border px-3 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {sourcesCopy.panel.forgetToken}
                </button>
              </div>
            </div>
          )}

          {source === 'figma_file' && (
            <div
              className="mt-4 rounded-xl border border-border bg-background p-4"
              data-testid="studio-import-figma-file-panel"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {sourcesCopy.panel.localFigmaFile}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    {sourcesCopy.panel.figmaFileNote}
                  </p>
                </div>
                <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted">
                  <FileArchive className="h-3.5 w-3.5" />
                  Choose .fig
                  <input
                    type="file"
                    accept=".fig,.pen"
                    className="sr-only"
                    data-testid="studio-import-figma-file-input"
                    onChange={(event) => {
                      const picked = event.target.files?.[0] ?? null
                      event.target.value = ''
                      if (!picked) {
                        setFigFile(null)
                        return
                      }
                      // UX audit finding 4 — a forced non-source file is
                      // refused here, so it is never uploaded and its name
                      // never becomes a Design source title.
                      const gate = checkOpenPencilSourceFile({
                        name: picked.name,
                        size: picked.size,
                      })
                      if (!gate.ok) {
                        setFigFile(null)
                        setImportFailure({
                          reason: gate.reason,
                          fileName: picked.name,
                        })
                        setOpenPencilImportActivity({
                          phase: 'blocked',
                          fileName: picked.name,
                          reason: gate.reason,
                          title: getOpenPencilImportFailureCopy(
                            locale,
                            gate.reason,
                          ).title,
                          startedAt: Date.now(),
                          updatedAt: Date.now(),
                        })
                        setMessage(
                          getOpenPencilImportFailureCopy(locale, gate.reason)
                            .title,
                        )
                        return
                      }
                      setImportFailure(null)
                      setFigFile(picked)
                    }}
                  />
                </label>
              </div>
              <div className="mt-3 rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
                {figFile
                  ? `${figFile.name} · ${Math.max(1, Math.round(figFile.size / 1024))} KB`
                  : 'No local .fig file selected.'}
              </div>
              {/* The card's destination, named and reachable.
                  `/ide/source-workspace` is the ONE route that enters the IDE
                  shell without a project, and the canvas / tokens / components
                  views mount nowhere else — yet nothing in the dashboard
                  linked to it, so a design imported earlier could not be
                  reopened from the page that offers importing designs. The
                  existing helper owns the route and the rail state; this is a
                  door, not a second copy of them. */}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => activateOpenPencilSourceWorkspace()}
                  data-testid="studio-import-open-source-workspace"
                  className="inline-flex h-9 items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                  {sourceDoorsCopy.openCanvas}
                </button>
                <span className="text-xs leading-5 text-muted-foreground">
                  {openPencilImport || registeredFigmaSource
                    ? sourceDoorsCopy.openCanvasWithSource
                    : sourceDoorsCopy.openCanvasEmpty}
                </span>
              </div>
            </div>
          )}

          {source === 'document_file' && (
            <div
              className="mt-4 rounded-xl border border-border bg-background p-4"
              data-testid="studio-import-document-panel"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    {sourcesCopy.panel.localDocument}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    {sourcesCopy.panel.documentFileNote}
                  </p>
                </div>
                <label className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted">
                  <FileText className="h-3.5 w-3.5" />
                  {docxCopy.empty.action}
                  <input
                    type="file"
                    accept={DOCUMENT_SOURCE_ACCEPT}
                    className="sr-only"
                    data-testid="studio-import-document-file-input"
                    onChange={(event) => {
                      const picked = event.target.files?.[0] ?? null
                      event.target.value = ''
                      setDropNotice(null)
                      if (!picked) {
                        replaceDocumentFile(null)
                        return
                      }
                      // A creator can force any file past `accept`, so the
                      // extension is re-checked here and refused BEFORE the
                      // name becomes a document source title.
                      if (!documentFormatForFileName(picked.name)) {
                        replaceDocumentFile(null)
                        setDocReadback({
                          ok: false,
                          format: null,
                          fileName: picked.name,
                          byteSize: picked.size,
                          errorCode: 'unsupported-format',
                          message: `${fileExtensionOf(picked.name) || 'This file'} cannot be read here. Supported: ${DOCUMENT_SOURCE_EXTENSIONS.join(', ')}.`,
                        })
                        setMessage(getSourceDropCopy(locale).rejectedFile(picked.name))
                        return
                      }
                      replaceDocumentFile(picked)
                      setMessage(
                        `${picked.name} · ${sourcesCopy.panel.readDocument}`,
                      )
                    }}
                  />
                </label>
              </div>
              <div
                className="mt-3 rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground"
                data-testid="studio-import-document-selection"
              >
                {docFile
                  ? `${docFile.name} · ${Math.max(1, Math.round(docFile.size / 1024))} KB`
                  : openDocument
                    ? `${openDocument.fileName}. ${docxCopy.retention.stored}`
                    : sourcesCopy.help.document_file.title}
              </div>
              {dropNotice && (
                <p
                  className="mt-2 text-xs text-muted-foreground"
                  data-testid="studio-import-drop-notice"
                >
                  {dropNotice}
                </p>
              )}

              {/* What this source can and cannot do TODAY. Generation from a
                  document is not wired, and a card that stayed quiet about
                  that would be the same unkept promise as the front-door chip
                  that navigated and dropped the file. */}
              <div
                className="mt-3 grid gap-2 sm:grid-cols-2"
                data-testid="studio-import-document-capabilities"
              >
                <div className="rounded-lg border border-success/40 bg-success/5 px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-success">
                    {docxCopy.capabilitiesNow}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs leading-5 text-muted-foreground">
                    <li>{sourcesCopy.panel.documentFileNote}</li>
                    <li>{docxCopy.locator.placeholderDocx}</li>
                    <li>{docxCopy.retention.stored}</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {docxCopy.capabilitiesNotYet}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs leading-5 text-muted-foreground">
                    <li>{sourcesCopy.panel.documentFileNote}</li>
                    <li>{getSourceSurfaceCopy(locale).durability.browserOnly}</li>
                    <li>{docxCopy.honestLabel}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* The refusal and the open document are SEPARATE facts, so they are
              separate panels. Rendering them as one either/or hid a document
              that was still open (the selection line said so while the
              structure had vanished) behind a message about a different file. */}
          {source === 'document_file' && docReadback && !docReadback.ok && (
            <div
              className="mt-4 rounded-xl border border-destructive/50 bg-destructive/5 px-4 py-3"
              data-testid="studio-import-document-error"
            >
              <div className="text-sm font-semibold text-destructive">
                {docxCopy.error.couldNotRead(docReadback.fileName)}
              </div>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {docReadback.message}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {docxCopy.error.failClosed}
              </p>
            </div>
          )}

          {source === 'document_file' &&
            openDocument &&
            (!docFile || docReadbackFile === docFile) && (
            // The SAME viewer the Design ▸ Canvas surface mounts, bound to the
            // same session — not a second, drifting readback panel. Its own
            // empty-state picker is hidden because the card above already owns
            // choosing a file.
            <div className="mt-4" data-testid="studio-import-document-readback">
              <DocxSourceViewer hideEmptyState />
            </div>
          )}

          {importFailure && (
            <div className="mt-4" data-testid="studio-import-failure-panel">
              <OpenPencilImportFailureNotice
                reason={importFailure.reason}
                fileName={importFailure.fileName}
                detail={importFailure.detail ?? null}
                testId="studio-import-failure"
              />
            </div>
          )}

          {openPencilImport && !importFailure && (
            <div
              className="mt-4"
              data-testid="studio-import-openpencil-readback"
            >
              <OpenPencilFigmaSourcePanels bundle={openPencilImport} compact />
            </div>
          )}

          <label className="mt-4 block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {sourcesCopy.panel.tags}
            </span>
            <input
              value={tags}
              onChange={(event) => setTags(event.target.value)}
              data-testid="studio-import-tags"
              className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/40 focus:ring-2 focus:ring-border/60"
            />
          </label>
        </div>

        <aside className="space-y-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            {/* Names the selection and says what happens to it. It used
                to also repeat the card's own description, `bestFor` and
                `output` verbatim, a few hundred pixels under the highlighted
                card that already said all three. What stays here is only what
                the card does NOT say. */}
            <div className="bg-background p-5" data-testid="studio-import-selected-source">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-background/95 text-foreground shadow-sm">
                <ActiveSourceIcon className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-sm font-semibold text-foreground">
                {sourcesCopy.cards[activeSource.id]?.label ?? activeSource.label}
              </h2>
              {/* What this source PRODUCES. It moved off the card, where it was
                  the fifth fragment on a 7-way scan and could not help anyone
                  choose, to the one place it is acted on. Shown HERE only — that
                  is not the repetition removed earlier, which was the same value
                  in two places at once. */}
              <p className="mt-2 text-xs font-semibold text-foreground/70">
                {sourcesCopy.cards[activeSource.id]?.output ?? activeSource.output}
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {source === 'figma_file'
                  ? sourcesCopy.panel.figmaFileNote
                  : source === 'document_file'
                    ? sourcesCopy.panel.documentFileNote
                    : sourcesCopy.panel.librarySuffix}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground">
              <FileText className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-sm font-semibold text-foreground">
              {sourcesCopy.after.title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {source === 'figma_file'
                ? sourcesCopy.after.figmaFileNote
                : source === 'document_file'
                  ? sourcesCopy.after.documentFileNote
                  : sourcesCopy.after.defaultNote}
            </p>
            <div className="mt-5 space-y-2">
              {[
                source === 'figma_file'
                  ? ['1', sourcesCopy.after.steps.designRead]
                  : source === 'document_file'
                    ? ['1', sourcesCopy.after.steps.documentRead]
                    : ['1', sourcesCopy.after.steps.prepared],
                source === 'document_file'
                  ? ['2', sourcesCopy.after.steps.keptInBrowser]
                  : ['2', sourcesCopy.after.steps.sourceSaved],
                source === 'figma_file'
                  ? ['3', sourcesCopy.after.steps.mapperOpens]
                  : source === 'document_file'
                    ? ['3', sourcesCopy.after.steps.structureOpens]
                    : ['3', sourcesCopy.after.steps.libraryOpens],
              ].map(([step, label]) => (
                <div
                  key={step}
                  className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
                    {step}
                  </span>
                  <span className="font-medium text-foreground">{label}</span>
                  <CheckCircle2 className="ml-auto h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </div>

          {/* The bundle upload and the chat note are both "another way in",
              and each used to be its own card with its own `h2` at the rank of
              the path itself — so one goal was offered through three fronts,
              the last of which opened by telling the creator the first two had
              been optional. Neither is removed: the upload button is the only
              way an existing app bundle gets in, and chat reaches a surface no
              card here does. They are grouped, under one heading, after the
              path — an alternative reads as an alternative. */}
          <div
            className="rounded-xl border border-border bg-card p-5 shadow-sm"
            data-testid="studio-import-other-ways"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <UploadCloud className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-sm font-semibold text-foreground">
              {sourcesCopy.otherWays.title}
            </h2>
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <FileArchive className="h-4 w-4 text-muted-foreground" />
                  {sourcesCopy.upload.title}
                </h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {sourcesCopy.upload.body}
                </p>
                <CogoApplicationImportButton
                  showLabel
                  label={sourcesCopy.upload.button}
                  className="mt-3"
                  buttonClassName="inline-flex h-10 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                />
              </div>
              <div className="border-t border-border pt-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  {sourcesCopy.chatStart.title}
                </h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {sourcesCopy.chatStart.body}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-sm font-semibold text-foreground">
              {sourcesCopy.savedWith.title}
            </h2>
            <div className="mt-4 space-y-3 text-sm">
              {[
                sourcesCopy.savedWith.openLink,
                sourcesCopy.savedWith.savedFile,
                sourcesCopy.savedWith.sourceSnapshot,
                sourcesCopy.savedWith.launchCheck,
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
            <div
              className="mt-5 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
              role="status"
              data-testid="studio-import-advanced-status"
            >
              {/* The example is shown once. For every source but the brief it is
                  already the field's placeholder — echoing it here put the same
                  sentence on screen twice, one copy of which vanishes the moment
                  you type. The brief has no example in its placeholder, so this
                  is where its example lives. */}
              {hasSourceInput
                ? message
                : source === 'assistant_prompt'
                  ? sourcesCopy.help[source].example
                  : null}
            </div>
          </div>
        </aside>
      </section>
        </div>
      </details>
    </DashboardContainer>
  )
}
